/** One version tag recorded against a dataset/model catalogue item (`versionTags[]`). */
export interface DatasetVersionTag {
  /** User-defined free-text tag, e.g. "v1.0", "monsoon-2026". Also used verbatim as the S3 key prefix segment. */
  tag: string;
  /** ISO 8601 creation timestamp. */
  datetime: string;
}

/** Version-related fields persisted on the catalogue item (subset of getItemById response / partial updateItem payload). */
export interface DatasetVersionMetadata {
  currentVersion: string | null;
  versionTags: DatasetVersionTag[];
}

/** Extract { currentVersion, versionTags } from a raw catalogue item record (tolerates snake_case / missing fields). */
export function extractDatasetVersionMetadata(
  item: Record<string, unknown> | null | undefined,
): DatasetVersionMetadata {
  if (!item) {
    return { currentVersion: null, versionTags: [] };
  }

  const rawTags = item['versionTags'] ?? item['version_tags'];
  const versionTags: DatasetVersionTag[] = Array.isArray(rawTags)
    ? rawTags
        .filter((t): t is Record<string, unknown> => !!t && typeof t === 'object')
        .map((t) => ({
          tag: String(t['tag'] ?? '').trim(),
          datetime: String(t['datetime'] ?? '').trim(),
        }))
        .filter((t) => t.tag)
    : [];

  const currentVersionRaw = item['currentVersion'] ?? item['current_version'];
  const currentVersion =
    currentVersionRaw != null && String(currentVersionRaw).trim()
      ? String(currentVersionRaw).trim()
      : null;

  return { currentVersion, versionTags };
}

/**
 * UI-only guard: non-empty, <=63 chars, [A-Za-z0-9._-] only (no "/" or "\"), case-insensitive-unique.
 * The tag becomes a literal S3 key path segment, so this restricts it defensively; files-connect-api
 * itself imposes no such validation.
 * @returns an error message, or null when the candidate is valid.
 */
export function validateNewVersionTag(
  candidate: string,
  existingTags: DatasetVersionTag[],
): string | null {
  const trimmed = candidate.trim();
  if (!trimmed) {
    return 'Version tag is required.';
  }
  if (trimmed.length > 63) {
    return 'Version tag must be 63 characters or fewer.';
  }
  if (/[\\/]/.test(trimmed)) {
    return 'Version tag cannot contain "/" or "\\".';
  }
  if (!/^[A-Za-z0-9._-]+$/.test(trimmed)) {
    return 'Version tag can only contain letters, numbers, dot, dash and underscore.';
  }
  if (existingTags.some((t) => t.tag.toLowerCase() === trimmed.toLowerCase())) {
    return `Version "${trimmed}" already exists. Choose a different tag.`;
  }
  return null;
}

function isBlank(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * `PUT /iudx/v2/cat/item` (dx-controlplane) validates the body against a `oneOf` DataBank /
 * AiModel / Apps schema, each with its own `required` field list (docs/openapi.yaml:
 * exampleEntityDataBank / exampleEntityAIModel). Existing items are sometimes missing one of
 * these (e.g. older items created without shortDescription/description/tags/yearRange), which
 * fails that validation on update. This fills any blank required field with a placeholder so
 * the update always includes a schema-valid payload — it never overwrites a real value.
 */
export function fillMandatoryItemFields(
  item: Record<string, unknown>,
): Record<string, unknown> {
  const rawType = item['type'];
  const typeArray = Array.isArray(rawType) ? rawType : rawType ? [rawType] : [];
  const isModel = typeArray.some((t) => String(t).toLowerCase().includes('aimodel'));

  const defaults: Record<string, unknown> = isModel
    ? {
        name: 'Untitled Model',
        type: ['adex:AiModel'],
        label: 'Untitled Model',
        shortDescription: 'No description provided.',
        description: 'No description provided.',
        tags: ['untagged'],
        accessPolicy: 'OPEN',
        organizationType: 'Public',
        organizationId: '',
        modelType: 'Custom',
        fileFormat: 'ipynb',
        industry: 'General',
        license: 'MIT',
        fileSize: '0MB',
      }
    : {
        name: 'Untitled Dataset',
        type: ['adex:DataBank'],
        shortDescription: 'No description provided.',
        description: 'No description provided.',
        tags: ['untagged'],
        organizationId: '',
        accessPolicy: 'OPEN',
        organizationType: 'Public',
        fileFormat: 'csv',
        industry: 'General',
        yearRange: 'NA',
        verifiedBy: 'NA',
        uploadFrequency: 'OneTime',
        license: 'Open Government License, India',
      };

  const filled: Record<string, unknown> = { ...item };
  for (const [key, fallback] of Object.entries(defaults)) {
    if (isBlank(filled[key])) {
      filled[key] = fallback;
    }
  }
  return filled;
}
